/**
 * Phase 1.7 — Documents module index, v2.
 * Group-by: processing_status (Processed / Needs Review / Processing / Failed).
 * KPI: Processed | Needs Review | Failed per spec.
 *
 * 2026-05-07 fix — three latent bugs were silencing every uploaded
 * document on this page:
 *
 *   1. The list call hit `/api/documents`, which doesn't exist. The
 *      real surface is `/api/docs` (audit §1 fixed the taxonomy
 *      cousin; this is the same shape of error one route over).
 *   2. The `processing_status` enum here only knew the legacy
 *      `'processed' | 'pending' | 'error'` triple. The schema (and
 *      every Phase 1.3+ code path) emits six values:
 *      pending, extracting, classifying, done, failed, needs_review.
 *      So `out[d.processing_status].push(d)` threw the same
 *      "Cannot read properties of undefined (reading 'push')" the
 *      animals page hit (audit §7).
 *   3. The card rendered `d.file_name` and `d.date_received`. The
 *      real columns are `original_filename` and `date_on_document`.
 *
 * Fix: hit the real endpoint, fold the six API statuses into four
 * display buckets (Needs Review / Processing / Processed / Failed),
 * and read the canonical column names with legacy fallbacks.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, EmptyState, FilterBar, KpiCard, SectionHeader, ViewToggle, type ViewMode } from '@/components/v2';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';
import type { Document } from '@shared/types';

type ApiStatus = 'pending' | 'extracting' | 'classifying' | 'done' | 'failed' | 'needs_review';
type Bucket = 'review' | 'in_progress' | 'processed' | 'failed';

// Map every API value (including legacy 'processed' / 'error' just in
// case a stale row sneaks through) into one of four UI buckets.
const BUCKET_OF: Record<string, Bucket> = {
  // Canonical Phase 1.3+ values
  pending: 'in_progress',
  extracting: 'in_progress',
  classifying: 'in_progress',
  needs_review: 'review',
  done: 'processed',
  failed: 'failed',
  // Legacy values — keep mapping so old rows don't crash the page.
  processed: 'processed',
  error: 'failed',
};

const BUCKET_ORDER: Bucket[] = ['review', 'in_progress', 'processed', 'failed'];
const BUCKET_LABEL: Record<Bucket, string> = {
  review: 'Needs Review',
  in_progress: 'Processing',
  processed: 'Processed',
  failed: 'Failed',
};
const BUCKET_TONE: Record<Bucket, string> = {
  review: 'bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark',
  in_progress: 'bg-sky-muted/10 text-sky-muted dark:bg-sky-muted/20 dark:text-sky-muted',
  processed: 'bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark',
  failed: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
};

const STATUS_FILTER_OPTIONS: { value: 'all' | ApiStatus; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'pending', label: 'Pending' },
  { value: 'extracting', label: 'Extracting' },
  { value: 'classifying', label: 'Classifying' },
  { value: 'done', label: 'Done' },
  { value: 'failed', label: 'Failed' },
];

const VIEW_KEY = 'documents';

// Read a column with a legacy fallback so the UI keeps working
// against either the canonical or the legacy field name.
function fileNameOf(d: Document): string {
  return (d.original_filename ?? d.file_name ?? '') as string;
}
function dateOf(d: Document): string {
  return (d.date_on_document ?? d.date_received ?? '') as string;
}

function bucketOf(d: Document): Bucket {
  return BUCKET_OF[d.processing_status as string] ?? 'in_progress';
}

export function DocumentsPageV2(): JSX.Element {
  const navigate = useNavigate();
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_KEY] ?? 'list';
  const [docs, setDocs] = useState<Document[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | ApiStatus>('all');

  usePageHeader(
    'Documents',
    <Button
      asChild
      size="sm"
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <a href="/documents/upload">
        <Upload className="h-4 w-4" />
        Upload
      </a>
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        // CORRECT path is /api/docs, not /api/documents — see header comment.
        const data = await authFetch<{ items: Document[] }>('/api/docs');
        if (!cancelled) setDocs(data.items ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load documents');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo<Document[]>(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (q && !`${d.title ?? ''} ${fileNameOf(d)}`.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'all' && d.processing_status !== statusFilter) return false;
      return true;
    });
  }, [docs, search, statusFilter]);

  // Group filtered docs into the four display buckets. Default each
  // bucket to [] so an unknown status can never resolve to undefined.
  const grouped = useMemo<Record<Bucket, Document[]>>(() => {
    const out: Record<Bucket, Document[]> = {
      review: [],
      in_progress: [],
      processed: [],
      failed: [],
    };
    for (const d of filtered) {
      const b = bucketOf(d);
      out[b].push(d);
    }
    return out;
  }, [filtered]);

  const totals = useMemo(() => {
    let processed = 0;
    let review = 0;
    let failed = 0;
    for (const d of docs) {
      const b = bucketOf(d);
      if (b === 'processed') processed += 1;
      else if (b === 'review') review += 1;
      else if (b === 'failed') failed += 1;
    }
    return { processed, review, failed };
  }, [docs]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || statusFilter !== 'all';
  const clear = (): void => { setSearch(''); setStatusFilter('all'); };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label="Documents"
          icon={FileText}
          primary={{ label: 'Processed', value: totals.processed, tone: 'positive' }}
          secondary={{
            label: 'Needs Review',
            value: totals.review,
            tone: totals.review > 0 ? 'warning' : 'default',
          }}
        />
        <KpiCard label="Filter" primary={{ label: 'Showing', value: filtered.length }} secondary={{ label: 'Total', value: docs.length }} />
        <KpiCard label="Errors" primary={{ label: 'Processing failed', value: totals.failed, tone: 'warning' }} />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search title or filename' }}
        filters={[
          <FilterSelect
            key="status"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as 'all' | ApiStatus)}
            options={STATUS_FILTER_OPTIONS}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">{error}</div> : null}

      {loading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={hasActiveFilters ? 'No matches' : 'No documents yet'}
          description={hasActiveFilters ? 'Try clearing filters.' : 'Upload your first document to start the OCR + classification pipeline.'}
          action={
            hasActiveFilters ? (
              <Button onClick={clear} variant="outline">Clear filters</Button>
            ) : (
              <Button asChild>
                <a href="/documents/upload">
                  <Plus className="h-4 w-4" />
                  Upload document
                </a>
              </Button>
            )
          }
        />
      ) : (
        BUCKET_ORDER.filter((b) => grouped[b].length > 0).map((bucket) => (
          <SectionHeader key={bucket} title={BUCKET_LABEL[bucket]} count={grouped[bucket].length} collapsible>
            {view === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {grouped[bucket].map((d) => {
                  const fname = fileNameOf(d);
                  const date = dateOf(d);
                  const b = bucketOf(d);
                  return (
                    <Card
                      key={d.id}
                      interactive
                      onClick={() => navigate(`/documents/${d.id}`)}
                      title={d.title || fname || 'Untitled'}
                      subtitle={`${fname || '–'} · ${formatBytes(d.file_size)}`}
                      badge={
                        <span className={`rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${BUCKET_TONE[b]}`}>
                          {BUCKET_LABEL[b]}
                        </span>
                      }
                      footer={
                        <div className="flex items-center justify-between text-xs">
                          <span>{date || '–'}</span>
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                        </div>
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border bg-surface dark:bg-surface-dark dark:border-border">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-raised text-left dark:bg-surface-raised-dark">
                    <tr>
                      <Th>Title</Th>
                      <Th>Filename</Th>
                      <Th>Size</Th>
                      <Th>Date</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[bucket].map((d) => {
                      const fname = fileNameOf(d);
                      const date = dateOf(d);
                      const b = bucketOf(d);
                      return (
                        <tr
                          key={d.id}
                          className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-raised dark:hover:bg-surface-raised-dark"
                          onClick={() => navigate(`/documents/${d.id}`)}
                        >
                          <Td className="font-medium">{d.title || '–'}</Td>
                          <Td className="font-mono text-xs">{fname || '–'}</Td>
                          <Td className="text-muted-foreground">{formatBytes(d.file_size)}</Td>
                          <Td className="text-muted-foreground">{date || '–'}</Td>
                          <Td>
                            <span className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${BUCKET_TONE[b]}`}>
                              {BUCKET_LABEL[b]}
                            </span>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </SectionHeader>
        ))
      )}
    </div>
  );
}

function formatBytes(n: number): string {
  if (!n) return '–';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function FilterSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  return <th className="px-4 py-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground">{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;
}
